import type {
  AttachmentAdapter,
  CompleteAttachment,
  PendingAttachment,
} from "@assistant-ui/react";

/**
 * Attachment validation configuration
 */
export interface AttachmentValidationConfig {
  /**
   * Maximum file size in bytes
   * @default 50 * 1024 * 1024 (50MB)
   */
  maxFileSizeBytes?: number;

  /**
   * Allowed MIME types (e.g., ['image/png', 'image/jpeg'])
   * If not provided, defaults to common document and image types
   */
  allowedMimeTypes?: string[];

  /**
   * Allowed file extensions (e.g., ['.pdf', '.docx'])
   * If not provided, defaults to common document and image extensions
   */
  allowedExtensions?: string[];

  /**
   * Maximum number of attachments per message
   * @default 5
   */
  maxAttachments?: number;

  /**
   * Whether to validate file content type matches extension
   * @default true
   */
  validateContentType?: boolean;
}

/**
 * Attachment validation error
 */
export class AttachmentValidationError extends Error {
  constructor(
    message: string,
    public readonly code:
      | "FILE_TOO_LARGE"
      | "INVALID_TYPE"
      | "TOO_MANY_FILES"
      | "VALIDATION_FAILED"
      | "VISION_NOT_SUPPORTED",
    public readonly file?: File,
  ) {
    super(message);
    this.name = "AttachmentValidationError";
  }
}

/**
 * Attachment adapter options
 */
export interface AttachmentAdapterOptions {
  /**
   * Validation configuration
   */
  validation?: AttachmentValidationConfig;

  /**
   * Whether the current model supports vision/image inputs.
   * When false, image attachments will be rejected with a clear error.
   * @default true
   */
  supportsVision?: boolean;

  /**
   * Callback when validation fails
   * Allows custom error handling (e.g., show toast notification)
   */
  onValidationError?: (error: AttachmentValidationError) => void;

  /**
   * Callback when attachment is added
   */
  onAttachmentAdded?: (attachment: PendingAttachment) => void;

  /**
   * Callback when attachment is removed
   */
  onAttachmentRemoved?: (attachmentId: string) => void;
}

/**
 * Default allowed MIME types
 */
const DEFAULT_ALLOWED_MIME_TYPES = [
  // Images
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  // Documents
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // Email
  "message/rfc822",
  "application/vnd.ms-outlook",
  // Text
  "text/plain",
  "text/markdown",
  "text/csv",
  "application/json",
  // Archives
  "application/zip",
  "application/x-zip-compressed",
];

/**
 * Default allowed file extensions
 */
const DEFAULT_ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".txt",
  ".md",
  ".csv",
  ".json",
  ".eml",
  ".msg",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".svg",
  ".zip",
];

/**
 * Default max file size (10MB)
 */
const DEFAULT_MAX_FILE_SIZE = 50 * 1024 * 1024;

/**
 * Format bytes to human-readable size
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): string {
  const ext =
    filename.lastIndexOf(".") >= 0
      ? filename.slice(filename.lastIndexOf(".")).toLowerCase()
      : "";
  return ext;
}

/**
 * Check if MIME type matches extension (basic validation)
 */
function mimeMatchesExtension(mimeType: string, extension: string): boolean {
  const mimeToExtMap: Record<string, string[]> = {
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/gif": [".gif"],
    "image/webp": [".webp"],
    "image/svg+xml": [".svg"],
    "application/pdf": [".pdf"],
    "text/plain": [".txt"],
    "text/markdown": [".md"],
    "text/csv": [".csv"],
    "application/json": [".json"],
    "message/rfc822": [".eml"],
    "application/vnd.ms-outlook": [".msg"],
  };

  const validExtensions = mimeToExtMap[mimeType];
  if (!validExtensions) {
    // Unknown MIME type, allow it
    return true;
  }

  return validExtensions.includes(extension.toLowerCase());
}

/**
 * Generate a secure attachment ID
 */
function generateAttachmentId(): string {
  // Use crypto.randomUUID if available
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return "attachment-" + crypto.randomUUID();
  }

  // Fallback
  return (
    "attachment-" + Date.now() + "-" + Math.random().toString(36).slice(2, 11)
  );
}

/**
 * Create an attachment adapter with enhanced validation
 */
export function createAttachmentAdapter(
  options: AttachmentAdapterOptions = {},
): AttachmentAdapter {
  const {
    validation = {},
    supportsVision = true,
    onValidationError,
    onAttachmentAdded,
    onAttachmentRemoved,
  } = options;

  const {
    maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE,
    allowedMimeTypes = DEFAULT_ALLOWED_MIME_TYPES,
    allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
    validateContentType = true,
  } = validation;

  // Build accept string from allowed extensions
  const acceptString = allowedExtensions.join(",");

  /**
   * Validate a file before adding it as an attachment
   */
  function validateFile(file: File): void {
    // Check file size
    if (file.size > maxFileSizeBytes) {
      const error = new AttachmentValidationError(
        'File "' +
          file.name +
          '" is too large (' +
          formatFileSize(file.size) +
          "). Maximum size is " +
          formatFileSize(maxFileSizeBytes) +
          ".",
        "FILE_TOO_LARGE",
        file,
      );
      onValidationError?.(error);
      throw error;
    }

    // Check vision support for image files
    if (!supportsVision && file.type.startsWith("image/")) {
      const error = new AttachmentValidationError(
        "The current model does not support image inputs. Please select a model with vision capabilities to attach images.",
        "VISION_NOT_SUPPORTED",
        file,
      );
      onValidationError?.(error);
      throw error;
    }

    // Check extension
    const extension = getFileExtension(file.name);
    if (
      extension &&
      !allowedExtensions.some((ext) => ext.toLowerCase() === extension)
    ) {
      const error = new AttachmentValidationError(
        'File type "' +
          extension +
          '" is not allowed. Allowed types: ' +
          allowedExtensions.join(", "),
        "INVALID_TYPE",
        file,
      );
      onValidationError?.(error);
      throw error;
    }

    // Check MIME type
    if (
      file.type &&
      !allowedMimeTypes.some((mime) => {
        // Support wildcards like 'image/*'
        if (mime.endsWith("/*")) {
          const prefix = mime.slice(0, -1);
          return file.type.startsWith(prefix);
        }
        return file.type === mime;
      })
    ) {
      const error = new AttachmentValidationError(
        'File type "' + file.type + '" is not allowed.',
        "INVALID_TYPE",
        file,
      );
      onValidationError?.(error);
      throw error;
    }

    // Validate MIME type matches extension
    if (validateContentType && file.type && extension) {
      if (!mimeMatchesExtension(file.type, extension)) {
        const error = new AttachmentValidationError(
          'File extension "' +
            extension +
            '" does not match content type "' +
            file.type +
            '".',
          "VALIDATION_FAILED",
          file,
        );
        onValidationError?.(error);
        throw error;
      }
    }
  }

  return {
    accept: acceptString,

    async add({ file }): Promise<PendingAttachment> {
      // Validate the file
      validateFile(file);

      // Determine attachment type based on file
      let type: "image" | "document" | "file" = "file";

      if (file.type.startsWith("image/")) {
        type = "image";
      } else if (
        file.type.includes("pdf") ||
        file.type.includes("document") ||
        file.type.includes("text") ||
        file.type.includes("zip") ||
        file.name.endsWith(".pdf") ||
        file.name.endsWith(".docx") ||
        file.name.endsWith(".txt") ||
        file.name.endsWith(".md") ||
        file.name.endsWith(".csv") ||
        file.name.endsWith(".json") ||
        file.name.endsWith(".eml") ||
        file.name.endsWith(".msg") ||
        file.name.endsWith(".zip")
      ) {
        type = "document";
      }

      const attachment: PendingAttachment = {
        id: generateAttachmentId(),
        type,
        name: file.name,
        contentType: file.type,
        file,
        status: { type: "requires-action", reason: "composer-send" },
      };

      onAttachmentAdded?.(attachment);

      return attachment;
    },

    async remove(attachment) {
      onAttachmentRemoved?.(attachment.id);
      // No other cleanup needed - files are handled by the browser
    },

    async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
      // For images, create a data URL
      if (attachment.type === "image" && attachment.file) {
        const dataURL = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error("Failed to read file"));
          reader.readAsDataURL(attachment.file!);
        });

        return {
          ...attachment,
          status: { type: "complete" },
          content: [
            {
              type: "image",
              image: dataURL,
            },
          ],
        };
      }

      // For other files, return as-is
      return {
        ...attachment,
        status: { type: "complete" },
        content: [],
      };
    },
  };
}
